// src/core/utils/http/api.js
import axios from 'axios';
import { getAuthApi } from '../context/auth-context';

const baseURL = '/api';

const api = axios.create({
    baseURL,
    withCredentials: true,
});

export const refreshClient = axios.create({
    baseURL,
    withCredentials: true,
});

let refreshPromise = null;

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error?.config || {};

        // Ignore canceled requests
        if (
            axios.isCancel?.(error) ||
            error?.name === 'CanceledError' ||
            error?.name === 'AbortError'
        ) {
            return Promise.reject(error);
        }

        // If we don't have a response (network error, CORS), just bubble up
        if (!error?.response) {
            return Promise.reject(error);
        }

        const { status } = error.response;

        // Avoid loops on the refresh endpoint itself
        const isRefreshCall =
            (config?.url &&
                (config.url.includes('/auth/refresh') || config.url.includes('/Login'))) ||
            config?._skipAuthRetry;

        if (status === 401 && !config._retry && !isRefreshCall) {
            config._retry = true;

            try {
                // De-duplicate concurrent 401s
                if (!refreshPromise) {
                    const { refresh } = getAuthApi(); // calls POST /api/auth/refresh and then /api/auth/me
                    refreshPromise = Promise.resolve().then(() => refresh());
                }
                await refreshPromise;
                refreshPromise = null;

                // Retry the original request once
                return api(config);
            } catch (e) {
                refreshPromise = null;

                // Refresh failed → logout + event so the UI can react (toasts/modals/redirect)
                try {
                    const { logout } = getAuthApi();
                    logout();
                } catch {
                    /* no-op */
                }

                window.dispatchEvent(new CustomEvent('api:auth-error'));
                return Promise.reject(e);
            }
        }

        // Keep your status-based events
        if (status === 403) window.dispatchEvent(new CustomEvent('api:forbidden-error'));
        if (status === 404) window.dispatchEvent(new CustomEvent('api:not-found-error'));
        if (status === 405) window.dispatchEvent(new CustomEvent('api:method-not-allowed-error'));
        if (status === 500) window.dispatchEvent(new CustomEvent('api:server-side-error'));

        return Promise.reject(error);
    }
);

export default api;
